import { useEffect, useState } from 'react'
import { usePreferences } from '../preferences/PreferencesContext'
import { resolveAssetsBase } from '../lib/reciters'

/** surah number (string) -> per-ayah duration in seconds (1-based ayah at index 0). */
type ReciterDurations = Record<string, number[]>

// Cache the (in-flight) fetch per reciter so each durations file is loaded
// once per session. The SW also CacheFirst-caches /audio/* so it's offline-safe.
const cache = new Map<string, Promise<ReciterDurations | null>>()

function loadReciterDurations(reciterId: string): Promise<ReciterDurations | null> {
  let promise = cache.get(reciterId)
  if (!promise) {
    promise = fetch(`${resolveAssetsBase()}/audio/durations/${reciterId}.json`)
      .then((res) => (res.ok ? (res.json() as Promise<ReciterDurations>) : null))
      .catch(() => null)
    cache.set(reciterId, promise)
  }
  return promise
}

export interface SurahTiming {
  /** Seconds elapsed across the WHOLE surah (sum of prior ayahs + current ayah). */
  elapsed: number
  /** Total surah duration in seconds. */
  total: number
}

/**
 * Real surah-wide playback timing, derived from the precomputed per-ayah
 * duration assets (`assets/audio/durations/<reciter>.json`, generated by
 * scripts/compute_audio_durations.py). Lets the player show the whole surah's
 * elapsed/total time instead of just the current ayah's length.
 *
 * Returns null until the durations file loads, or when it's unavailable for
 * the surah/reciter — callers should fall back to their previous display.
 */
export function useSurahTiming(
  surahNumber: number | null,
  currentAyahNumber: number | null,
  timeInAyah: number,
): SurahTiming | null {
  const { prefs } = usePreferences()
  const reciter = prefs.reciter
  const [durations, setDurations] = useState<ReciterDurations | null>(null)

  useEffect(() => {
    let alive = true
    void loadReciterDurations(reciter).then((d) => {
      if (alive) setDurations(d)
    })
    return () => {
      alive = false
    }
  }, [reciter])

  if (surahNumber === null || !durations) return null
  const ayahDurations = durations[String(surahNumber)]
  if (!ayahDurations || ayahDurations.length === 0) return null

  const total = ayahDurations.reduce((sum, d) => sum + d, 0)
  const index = Math.max(0, Math.min(ayahDurations.length, currentAyahNumber ?? 1) - 1)
  let elapsed = 0
  for (let i = 0; i < index; i++) elapsed += ayahDurations[i]
  elapsed += Math.min(Math.max(0, timeInAyah), ayahDurations[index] ?? 0)

  return { elapsed, total }
}
